import { TellescopeModelFromName, type useTellescopeMutation } from "@tellescope/tanstack-query";
import { FormTypesWithValues, TellescopeFieldResponse } from "../../types";
import { AnswerForType, Enduser, FormField, FormFieldOptions } from "@tellescope/types-models";
import type { NextFieldLogicOptions } from "../../utils/types";
import { useTellescopeFormCore, type LocalTellescopeFormResponseMap } from "../use-tellescope-form-core";
import { useSubmitForm } from "./use-single-page-submit-form";
import { useGetFieldProps } from "./use-get-field-props";
import { useSinglePageFormShallowStore, type SubmitPhase } from "../../store";

export type { LocalTellescopeFormResponseMap };

export interface UseTellescopeSinglePageFormArgs {
  formId: string;
  formTitle?: string;
  responseId?: string;
  enduserId: string;
  parentResponseId?: string;
  rootResponseId?: string;
  urlLogicValue?: string;
}

export interface UseTellescopeSinglePageFormReturn {
  currentResponses: LocalTellescopeFormResponseMap;
  validateField: <T extends keyof AnswerForType = keyof AnswerForType>(args: {
    fieldId: string;
    valueOverride?: AnswerForType[T];
  }) => Promise<string | null>;
  validateAllFields: () => Promise<boolean>;
  visibleFields: (FormField & { answer: TellescopeFieldResponse["answer"] })[];
  setFieldValue: <T extends FormTypesWithValues>(fieldId: string, value: AnswerForType[T]) => void;
  saveResponse: () => Promise<void>;
  submitForm: () => Promise<unknown>;
  submitFormMutation: ReturnType<typeof useTellescopeMutation<"form_responses", "submit_form_response">>;
  submitPhase: SubmitPhase;
  uploadProgress: { completed: number; total: number };
  isFieldValueValid: (fieldId: string) => boolean;
  form: TellescopeModelFromName<"forms"> | undefined;
  fieldErrors: Record<string, string | null>;
  touchedFields: Set<string>;
  handleFieldBlur: (fieldId: string) => void;
  getFieldProps: <T extends FormTypesWithValues = FormTypesWithValues>(
    fieldId: string,
    type: T
  ) => {
    id: string;
    type: T;
    title: string;
    value: AnswerForType[T];
    onChange: (newValue: AnswerForType[T]) => void;
    onBlur?: () => void;
    error: string | null;
    touched: boolean;
    options?: FormFieldOptions;
    fullZIP?: boolean;
    disabled?: boolean;
    pendingFiles?: File[];
    onPendingFilesChange?: (files: File[]) => void;
  } | null;
  enduser: TellescopeModelFromName<"endusers"> | Enduser | undefined;
  isComplete: boolean;
  isSubmitted: boolean;
  isLoading: boolean;
  fieldsArr: TellescopeModelFromName<"form_fields">[];
  logicOptions: NextFieldLogicOptions;
  repeatInputs: Record<string, string>;
  setRepeatValue: (fieldId: string, value: string) => void;
  handleRepeatBlur: (fieldId: string) => void;
  repeatTouchedFields: Set<string>;
  repeatMismatchFieldIds: Set<string>;
}

export const useTellescopeSinglePageForm = ({
  formId,
  formTitle,
  responseId,
  enduserId,
  parentResponseId,
  rootResponseId,
  urlLogicValue,
}: UseTellescopeSinglePageFormArgs): UseTellescopeSinglePageFormReturn => {
  const core = useTellescopeFormCore({
    formId,
    formTitle,
    responseId,
    enduserId,
    parentResponseId,
    rootResponseId,
    urlLogicValue,
    useFormShallowStore: useSinglePageFormShallowStore,
  });

  const submitForm = useSubmitForm({
    core,
    formId,
    enduserId,
    responseId,
    parentResponseId,
    rootResponseId,
  });

  const getFieldProps = useGetFieldProps({ core });

  return {
    currentResponses: core.currentResponses,
    validateField: core.validateField,
    validateAllFields: core.validateAllFields,
    visibleFields: core.visibleFields,
    setFieldValue: core.setFieldValue,
    saveResponse: core.saveResponse,
    submitForm,
    submitFormMutation: core.submitFormMutation,
    submitPhase: core.submitPhase,
    uploadProgress: core.uploadProgress,
    isFieldValueValid: core.isFieldValueValid,
    form: core.form,
    fieldErrors: core.fieldErrors,
    touchedFields: core.touchedFields,
    handleFieldBlur: core.handleFieldBlur,
    getFieldProps,
    enduser: core.enduser,
    isComplete: core.isComplete,
    isSubmitted: !!core.responseQuery.data?.submittedAt,
    isLoading: core.formFieldsQuery.isLoading,
    fieldsArr: core.fields?.fieldsArr ?? [],
    logicOptions: core.logicOptions,
    repeatInputs: core.repeatInputs,
    setRepeatValue: core.setRepeatValue,
    handleRepeatBlur: core.handleRepeatBlur,
    repeatTouchedFields: core.repeatTouchedFields,
    repeatMismatchFieldIds: core.repeatMismatchFieldIds,
  };
};
